import React, { useEffect, useMemo, useState, useRef, forwardRef, useCallback } from "react";
import { useErrorNotification } from "../../context/ErrorNotificationContext";
import { useBoolean } from "@fluentui/react-hooks";
import { 
  FontIcon, 
  Stack, 
  Text, 
  TooltipHost, 
  IconButton, 
  IContextualMenuProps, 
  ITooltipHostStyles, 
  IContextualMenuItem,
  Spinner,
  SpinnerSize
} from "@fluentui/react";
import {
  historyGiveFeedback
} from "../../api";
import styles from "./Answer.module.css";
import { AskResponse, Citation, Feedback } from "../../api";
import { ExportModal } from "../ExportModal";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import supersub from "remark-supersub";
import rehypeRaw from "rehype-raw";
import { rehypeHighlightSearch } from "../../util/rehypeHighlightSearch";

import * as sdk from "microsoft-cognitiveservices-speech-sdk";

import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";

declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
  }
}
interface Props {
  answer: AskResponse;
  onCitationClicked: (citedDocument: Citation) => void;
  onSpeak?: (index: number, action: string) => void;
  isActive?: boolean;
  index: number;
  canCopy: boolean;
  canDownload?: boolean;
  canGiveFeedback: boolean;
  onFeedbackUpdate?: (messageId: string, feedback: Feedback) => void;
  /** Optional search query for find-in-chat highlighting */
  findQuery?: string;
  /** Optional timestamp string to display at the bottom of the answer bubble */
  timestamp?: string;
  /** Optional model name to display in the answer footer */
  model?: string;
  onSuggestedQuestionClicked?: (question: string) => void;
}
const MyStackComponent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>((props, ref) => (
  <div {...props} ref={ref} />
));

// Define dislike menu items as a module-level constant (static array, no component state)
const DISLIKE_MENU_ITEMS = [
  { key: Feedback.OtherUnhelpful, text: 'Unhelpful' },
  { key: Feedback.MissingCitation, text: 'Missing citation' },
  { key: Feedback.WrongCitation, text: 'Wrong citation' },
  { key: Feedback.OutOfScope, text: 'Out of scope' },
  { key: Feedback.InaccurateOrIrrelevant, text: 'Inaccurate or irrelevant' },
  { key: Feedback.Manipulative, text: 'Manipulative' },
  { key: Feedback.OtherHarmful, text: 'Harmful' },
  { key: Feedback.HateSpeech, text: 'Hate speech' },
  { key: Feedback.Violent, text: 'Violent' },
  { key: Feedback.Sexual, text: 'Sexual' },
];

const calloutProps = { gapSpace: 0 };
const hostStyles: Partial<ITooltipHostStyles> = { root: { display: 'inline-block' } };

// Defined at module level so the reference is always stable (no remounts).
const LinkRenderer = ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { children?: React.ReactNode }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
    {children}
  </a>
);

export const Answer = ({
  answer,
  onCitationClicked,
  isActive,
  index,
  canCopy,
  canDownload = canCopy,
  canGiveFeedback,
  onFeedbackUpdate,
  findQuery,
  timestamp,
  model,
  onSuggestedQuestionClicked,
}: Props) => {
  const [isRefAccordionOpen, { toggle: toggleIsRefAccordionOpen }] =
    useBoolean(false);
  const [isSuggestedQuestionsExpanded, setIsSuggestedQuestionsExpanded] = useState(true);
  const suggestedQuestionSubmittedRef = useRef(false);

  const { showError } = useErrorNotification();
  const answerContainerRef = useRef<HTMLDivElement>(null); // read the text from the container
  const messageBoxId = "message-" + index;
  const [isSpeaking, setIsSpeaking] = useState(false); // for speaker on
  const [, setShowSpeaker] = useState(true); //for show and hide the speaker icon
  const [, setIsPaused] = useState(false); //for pause
  //const parsedAnswer = useMemo(() => parseAnswer(answer), [answer]);
  const [chevronIsExpanded, setChevronIsExpanded] =
    useState(isRefAccordionOpen);
  const refContainer = useRef<HTMLDivElement>(null);

  // Local state for feedback to trigger re-renders
  const [currentFeedback, setCurrentFeedback] = useState<Feedback | undefined>(answer.feedback);

  const [synthesizerData] = useState({
    key: "",
    region: "",
  });
  const [synthesizer, setSynthesizer] =
    useState<SpeechSDK.SpeechSynthesizer | null>(null);
  const [audioDestination, setAudioDestination] =
    useState<SpeechSDK.SpeakerAudioDestination | null>(null);
  const [playbackTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [, setRemainingDuration] = useState<number>(0);

  // Update local feedback state when prop changes
  useEffect(() => {
    setCurrentFeedback(answer.feedback);
  }, [answer.feedback]);

  // Define the dislike menu items with their corresponding feedback types
  const dislikeMenuItems = DISLIKE_MENU_ITEMS;

  // Helper function to check if current feedback is any of the negative feedback types
  const isNegativeFeedback = useMemo(() => {
    return currentFeedback && dislikeMenuItems.some(item => item.key === currentFeedback);
  }, [currentFeedback, dislikeMenuItems]);

  // Helper function to check if current feedback is positive
  const isPositiveFeedback = useMemo(() => {
    return currentFeedback === Feedback.Positive;
  }, [currentFeedback]);


  const handleChevronClick = () => {
    setChevronIsExpanded(!chevronIsExpanded);
    toggleIsRefAccordionOpen();
  };

  const initializeSynthesizer = () => {
    const speechConfig = sdk.SpeechConfig.fromSubscription(
      synthesizerData.key,
      synthesizerData.region
    );
    const newAudioDestination = new SpeechSDK.SpeakerAudioDestination();
    const audioConfig =
      SpeechSDK.AudioConfig.fromSpeakerOutput(newAudioDestination);
    const newSynthesizer = new SpeechSDK.SpeechSynthesizer(
      speechConfig,
      audioConfig
    );
    setSynthesizer(newSynthesizer);
    setAudioDestination(newAudioDestination);
    if (playbackTimeout) {
      clearTimeout(playbackTimeout);
    }
    setRemainingDuration(0);
  };

  useEffect(() => {
    if (synthesizerData.key != "") {
      initializeSynthesizer();

      return () => {
        if (synthesizer) {
          synthesizer.close();
        }
        if (audioDestination) {
          audioDestination.close();
        }
        if (playbackTimeout) {
          clearTimeout(playbackTimeout);
        }
      };
    }
  }, [index, synthesizerData]); // eslint-disable-line react-hooks/exhaustive-deps

  //useEffect(() => {
  //  const fetchSythesizerData = async () => {
  //    const response = await fetch("/api/speech");
  //    try {
  //      if (!response.ok) {
  //       throw new Error("Network response was not ok");
  //      }
  //    const data = await response.json();
  //    setSynthesizerData({ key: data.key, region: data.region });
  //    } catch(e) {
  //      console.log(e)
  //    }
  //  };
  //  fetchSythesizerData();
  //}, []);

  useEffect(() => {
    if (!isActive && synthesizer && isSpeaking) {
      resetSpeech();
    }
  }, [isActive, synthesizer, isSpeaking]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setChevronIsExpanded(isRefAccordionOpen);
    // if (chevronIsExpanded && refContainer.current) {
    //   refContainer.current.scrollIntoView({ behavior: 'smooth' });
    // }
    // After genrating answer then only show speaker icon
    if (answer.answer === "Generating answer...") {
      setShowSpeaker(false);
    } else {
      setShowSpeaker(true);
    }
  }, [answer, isRefAccordionOpen]);

  const createCitationFilepath = (
    citation: Citation,
    index: number,
  ) => {
    let citationFilename = "";

    if (citation.title != null) {
      citationFilename = citation.title;
    } else {
      citationFilename = `Citation ${index}`;
    }
    return citationFilename;
  };

  const resetSpeech = () => {
    //audioDestination?.close();
    audioDestination?.pause();
    setIsSpeaking(false);
    setIsPaused(false);
    //synthesizer?.close();
    initializeSynthesizer();
  };

  useEffect(() => {
    const handleCopy = () => {
      //alert("Please consider where you paste this content.");
    };
    const messageBox = document.getElementById(messageBoxId);
    messageBox?.addEventListener("copy", handleCopy);
    return () => {
      messageBox?.removeEventListener("copy", handleCopy);
    };
  }, [messageBoxId]);

  const copyAnswer = async () => {
    // Build citation sources suffix
    let sourcesSuffix = '';
    let sourcesHtmlSuffix = '';
    if (answer.citations && answer.citations.length > 0) {
      const citationUrls = answer.citations
        .filter(citation => citation.url && citation.url.trim() !== '')
        .map(citation => citation.url as string);
      if (citationUrls.length > 0) {
        sourcesSuffix = '\n\nSources:\n' + citationUrls.join('\n');
        sourcesHtmlSuffix = '<br><br><strong>Sources:</strong><br>' +
          citationUrls.map(u => `<a href="${u}">${u}</a>`).join('<br>');
      }
    }

    try {
      // Grab the rendered HTML from the markdown container (excludes toolbar buttons)
      const markdownEl = answerContainerRef.current?.querySelector('.answerText, [class*="answerText"]') as HTMLElement | null;
      let renderedHtml = markdownEl ? markdownEl.innerHTML : null;

      // Embed images as base64 data URIs so the copied HTML is self-contained and
      // does not break when the Azure Blob SAS token expires.
      // We clone the DOM node and set src directly on the clone's img elements —
      // this avoids the &amp; encoding mismatch that occurs with string replacement
      // (innerHTML encodes & as &amp; but imgEl.src returns the decoded URL).
      if (markdownEl) {
        const clone = markdownEl.cloneNode(true) as HTMLElement;
        const liveImgs = Array.from(markdownEl.querySelectorAll('img[src]')) as HTMLImageElement[];
        const cloneImgs = Array.from(clone.querySelectorAll('img[src]')) as HTMLImageElement[];

        await Promise.all(
          liveImgs.map(async (liveImg, i) => {
            const cloneImg = cloneImgs[i];
            if (!cloneImg) return;
            const src = liveImg.src;
            if (!src || src.startsWith('data:')) return;

            // Attempt 1: draw the already-loaded img to a canvas.
            // Works when the image was loaded from the same origin or when CORS
            // headers are present on the blob storage. canvas.toDataURL() throws a
            // SecurityError if the canvas is tainted (cross-origin without CORS).
            try {
              if (liveImg.complete && liveImg.naturalWidth > 0) {
                const canvas = document.createElement('canvas');
                canvas.width = liveImg.naturalWidth;
                canvas.height = liveImg.naturalHeight;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                  ctx.drawImage(liveImg, 0, 0);
                  cloneImg.src = canvas.toDataURL('image/png'); // throws if tainted
                  return;
                }
              }
            } catch {
              // Canvas tainted (cross-origin image without CORS headers) — fall through to fetch
            }

            // Attempt 2: fetch the image directly.
            // Works when CORS is configured on the Azure Blob Storage account.
            try {
              const response = await fetch(src);
              if (!response.ok) return;
              const blob = await response.blob();
              cloneImg.src = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            } catch {
              // Fetch failed (CORS not configured on blob storage) — keep the original
              // SAS URL in the clone. The pasted HTML will still show the image as long
              // as the SAS token has not expired in the paste target application.
            }
          })
        );

        renderedHtml = clone.innerHTML;
      }

      if (navigator.clipboard && (window as unknown as { ClipboardItem?: unknown }).ClipboardItem) {
        // Modern API: write both HTML (for rich paste targets like Word/Outlook)
        // and plain text (for plain-text targets like Notepad/code editors).
        const htmlContent = renderedHtml
          ? `<html><body>${renderedHtml}${sourcesHtmlSuffix}</body></html>`
          : `<html><body><pre>${answer.answer}</pre>${sourcesHtmlSuffix}</body></html>`;

        // Derive clean plain text from the rendered DOM so it matches what the user sees
        const plainText = markdownEl
          ? (markdownEl.innerText ?? markdownEl.textContent ?? answer.answer) + sourcesSuffix
          : answer.answer + sourcesSuffix;

        const ClipboardItemCtor = (window as unknown as { ClipboardItem: typeof ClipboardItem }).ClipboardItem;
        await navigator.clipboard.write([
          new ClipboardItemCtor({
            'text/html': new Blob([htmlContent], { type: 'text/html' }),
            'text/plain': new Blob([plainText], { type: 'text/plain' }),
          }),
        ]);
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        // Fallback: plain text only
        const plainText = markdownEl
          ? (markdownEl.innerText ?? markdownEl.textContent ?? answer.answer) + sourcesSuffix
          : answer.answer + sourcesSuffix;
        await navigator.clipboard.writeText(plainText);
      } else {
        // Legacy fallback for older browsers
        const plainText = markdownEl
          ? (markdownEl.innerText ?? markdownEl.textContent ?? answer.answer) + sourcesSuffix
          : answer.answer + sourcesSuffix;
        const textArea = document.createElement('textarea');
        textArea.value = plainText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        if (!document.execCommand('copy')) {
          showError("Failed to copy answer to clipboard.");
        }
        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error('Failed to copy text: ', error);
      showError("Failed to copy answer to clipboard.");
    }
  }

  const likeAnswer = async () => {
    if (isPositiveFeedback) {
      return;
    }

    console.log("like");

    // Update local state immediately for UI responsiveness
    setCurrentFeedback(Feedback.Positive);

    // Notify parent component if callback exists
    if (onFeedbackUpdate && answer.messageId) {
      onFeedbackUpdate(answer.messageId, Feedback.Positive);
    }

    // Send feedback to server
    try {
      console.log(answer);
      await historyGiveFeedback(answer.conversationId, answer.messageId, Feedback.Positive);
    } catch (error) {
      console.error("Failed to send feedback:", error);
      // Optionally revert the UI state on error
      setCurrentFeedback(answer.feedback);
    }
  }

  const dislikeAnswer = useCallback(
    (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, item?: IContextualMenuItem): boolean | void => {
      if (item === undefined) {
        return;
      }

      const dislikeReason = dislikeMenuItems.find(i => item.key === i.key)!.key;
      console.log("dislike", dislikeReason);

      setCurrentFeedback(dislikeReason);

      if (onFeedbackUpdate && answer.messageId) {
        onFeedbackUpdate(answer.messageId, dislikeReason);
      }

      console.log(answer);
      historyGiveFeedback(answer.conversationId, answer.messageId, dislikeReason)
        .catch(error => {
          console.error("Failed to send feedback:", error);
          setCurrentFeedback(answer.feedback);
        });
    },
    [answer, onFeedbackUpdate, dislikeMenuItems]
  );

  // Recompute menuProps every render so onClick always has fresh closure.
  const dislikeMenuProps: IContextualMenuProps = {
    items: dislikeMenuItems.map(item => ({
      key: item.key,
      text: item.text,
      onClick: dislikeAnswer
    })),
    directionalHintFixed: true,
    calloutProps: { calloutMaxHeight: 220 },
  };

  // State for full-size image modal
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // State for full-size image modal
  const [fullSizeImageSrc, setFullSizeImageSrc] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const dragOffsetStart = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 4));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.25));
  };

  const handleZoomReset = () => {
    setZoomLevel(1);
    setDragOffset({ x: 0, y: 0 });
  };

  const handleWheelZoom = (e: React.WheelEvent) => {
    e.stopPropagation();
    if (e.deltaY < 0) {
      setZoomLevel(prev => Math.min(prev + 0.15, 4));
    } else {
      setZoomLevel(prev => {
        const next = Math.max(prev - 0.15, 0.25);
        // Reset drag offset when zooming back to 1 or below
        if (next <= 1) setDragOffset({ x: 0, y: 0 });
        return next;
      });
    }
  };

  const handleCloseModal = () => {
    setFullSizeImageSrc(null);
    setZoomLevel(1);
    setDragOffset({ x: 0, y: 0 });
  };

  const handleImageMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel <= 1) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY };
    dragOffsetStart.current = { ...dragOffset };
  };

  const handleImageMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !dragStart.current) return;
    e.stopPropagation();
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setDragOffset({
      x: dragOffsetStart.current.x + dx,
      y: dragOffsetStart.current.y + dy,
    });
  };

  const handleImageMouseUp = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDragging(false);
    dragStart.current = null;
  };

  // useMemo gives a stable component reference so React never unmounts/remounts
  // the <img> element on Answer re-renders (feedback clicks, accordion toggles, etc.).
  // Remounting retries the fetch; if the SAS token has expired the retry fails.
  // useState setters (setFullSizeImageSrc, setZoomLevel, setDragOffset) are guaranteed
  // stable by React, so the empty deps array is intentional and correct.
  const ImageRenderer = useMemo(() => {
    function ImageRendererInner({ src, alt }: { src?: string; alt?: string }) {
      const [imageError, setImageError] = useState(false);
      const blobUrlRegex = /https:\/\/[a-z0-9]+\.blob\.core\.windows\.net\/[^\s"'<>)\\]+/i;
      const safeSrc = src ?? "";
      const isBlobUrl = blobUrlRegex.test(safeSrc);

      // Suppress broken Confluence-internal relative paths that resolve to 404 against the SWA domain
      const isScrollExternal = safeSrc.includes('_scroll_external/');

      // Reset error state whenever the URL changes (e.g. streaming replaces a partial
      // SAS URL with a fresh one at end-of-turn). Without this, a failed partial load
      // would permanently prevent the image from showing after the URL is refreshed.
      useEffect(() => {
        setImageError(false);
      }, [safeSrc]);

      if (isScrollExternal) {
        return null;
      }
      
      if (isBlobUrl && !safeSrc.includes('?')) {
        return <span style={{ fontSize: '14px', color: 'gray' }}>Loading Image...</span>;
      }

      if (imageError) {
        return <span style={{ fontSize: '14px', color: 'red' }}>Failed to load image</span>;
      }

      return (
        <img
          src={safeSrc}
          alt={alt}
          style={{ width: "80%", maxWidth: "700px", cursor: "zoom-in", display: "block" }}
          onError={() => setImageError(true)}
          onClick={() => { if (!imageError) { setFullSizeImageSrc(safeSrc); setZoomLevel(1); setDragOffset({ x: 0, y: 0 }); } }}
          title="Click to view full size"
        />
      );
    }
    return ImageRendererInner;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const markdownComponents = useMemo(() => ({
    a: LinkRenderer,
    img: ImageRenderer,
    // Render paragraphs as divs so that block-level children (img, div) don't
    // cause invalid HTML nesting (div/img inside p is not allowed).
    p: ({ node: _node, ...props }: React.HTMLAttributes<HTMLElement> & { node?: unknown }) => <div {...props} />,
  }), [ImageRenderer]);

  return (
    <>
      {/* Full-size image modal */}
      {fullSizeImageSrc && (
        <div
          onClick={!isDragging ? handleCloseModal : undefined}
          onWheel={handleWheelZoom}
          onMouseMove={handleImageMouseMove}
          onMouseUp={handleImageMouseUp}
          onMouseLeave={handleImageMouseUp}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: isDragging ? "grabbing" : "zoom-out",
            overflow: "hidden",
            userSelect: "none",
          }}
          role="dialog"
          aria-label="Full size image"
        >
          <img
            src={fullSizeImageSrc}
            alt="Full size"
            draggable={false}
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
              transform: `scale(${zoomLevel}) translate(${dragOffset.x / zoomLevel}px, ${dragOffset.y / zoomLevel}px)`,
              transition: isDragging ? "none" : "transform 0.2s ease",
              cursor: isDragging ? "grabbing" : zoomLevel > 1 ? "grab" : "default",
              pointerEvents: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={handleImageMouseDown}
          />

          {/* Zoom controls */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              bottom: 24,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              gap: 8,
              backgroundColor: "rgba(0,0,0,0.6)",
              borderRadius: 8,
              padding: "6px 12px",
            }}
          >
            <IconButton
              iconProps={{ iconName: "ZoomOut" }}
              title="Zoom out"
              ariaLabel="Zoom out"
              onClick={handleZoomOut}
              disabled={zoomLevel <= 0.25}
              styles={{ root: { color: "white" }, icon: { color: "white" } }}
            />
            <span
              style={{ color: "white", minWidth: 48, textAlign: "center", fontSize: 14, cursor: "pointer" }}
              onClick={handleZoomReset}
              title="Click to reset zoom and position"
            >
              {Math.round(zoomLevel * 100)}%
            </span>
            <IconButton
              iconProps={{ iconName: "ZoomIn" }}
              title="Zoom in"
              ariaLabel="Zoom in"
              onClick={handleZoomIn}
              disabled={zoomLevel >= 4}
              styles={{ root: { color: "white" }, icon: { color: "white" } }}
            />
          </div>

          {/* Close button */}
          <IconButton
            iconProps={{ iconName: "Cancel" }}
            title="Close"
            ariaLabel="Close full size image"
            onClick={(e) => { e.stopPropagation(); handleCloseModal(); }}
            style={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "white",
              backgroundColor: "rgba(0,0,0,0.5)",
            }}
          />
        </div>
      )}

      <MyStackComponent
        data-testid="message-box"
        className={styles.answerContainer}
        id={messageBoxId}
        ref={answerContainerRef}
      >
        {answer.answer === "Generating answer..." ? (
          <Stack.Item grow>
            <Spinner
              size={SpinnerSize.xSmall}
              label="Generating answer..."
              labelPosition="right"
              className={styles.loadingSpinner}
            />
          </Stack.Item>
          ) : (
          <Stack.Item grow>
            <ReactMarkdown
              remarkPlugins={[remarkGfm, supersub]}
              rehypePlugins={
                findQuery?.trim()
                  ? [rehypeRaw, [rehypeHighlightSearch, { query: findQuery }] as never]
                  : [rehypeRaw]
              }
              children={answer.answer}
              className={styles.answerText}
              components={markdownComponents}
            />
          </Stack.Item>
        )}

        {(canCopy || canGiveFeedback) &&
          (
            <Stack.Item className={styles.feedbackContainer}>
              {canDownload && (
                <span>
                  <TooltipHost
                    content="Download response"
                    closeDelay={100}
                    calloutProps={calloutProps}
                    styles={hostStyles}
                  >
                    <IconButton
                      role="button"
                      onKeyDown={(e) =>
                        e.key === " " || e.key === "Enter"
                          ? setIsExportModalOpen(true)
                          : null
                      }
                      onClick={() => setIsExportModalOpen(true)}
                      aria-label="download response"
                      className={styles.downloadButton}
                      iconProps={{ iconName: "Download" }}
                    />
                  </TooltipHost>
                </span>
              )}
              {canCopy && (
                <span>
                  <TooltipHost
                    content="Copy answer"
                    closeDelay={100}
                    calloutProps={calloutProps}
                    styles={hostStyles}
                  >
                    <IconButton
                      role="button"
                      onKeyDown={(e) =>
                        e.key === " " || e.key === "Enter"
                          ? copyAnswer()
                          : null
                      }
                      onClick={() => copyAnswer()}
                      aria-label="copy answer"
                      className={styles.copyButton}
                      iconProps={{ iconName: "Copy" }}
                    />
                  </TooltipHost>
                </span>
              )}
              {canGiveFeedback && (
                <span>                
                  <TooltipHost
                    content="Like this answer"
                    closeDelay={100}
                    calloutProps={calloutProps}
                    styles={hostStyles}
                  >
                    <IconButton
                      role="button"
                      onKeyDown={(e) =>
                        e.key === " " || e.key === "Enter"
                          ? likeAnswer()
                          : null
                      }
                      onClick={() => likeAnswer()}
                      aria-label="like answer"
                      className={styles.likeButton}
                      iconProps={{ iconName: isPositiveFeedback ? "LikeSolid" : "Like" }}
                    />
                  </TooltipHost>

                  <TooltipHost
                    content="Dislike this answer"
                    closeDelay={100}
                    calloutProps={calloutProps}
                    styles={hostStyles}
                  >
                    <IconButton
                      role="button"
                      aria-label="dislike answer"
                      className={styles.copyButton}
                      iconProps={{ iconName: isNegativeFeedback ? "DislikeSolid" : "Dislike" }}
                      menuProps={dislikeMenuProps}
                    />
                  </TooltipHost>
                </span>
              )}

            </Stack.Item>
          )
        }

        <Stack horizontal className={styles.answerFooter} verticalAlign="start">
          {/*<Stack.Item className={styles.answerDisclaimerContainer}>*/}
          {/*  <span*/}
          {/*    className={`${styles.answerDisclaimer} ${styles.mobileAnswerDisclaimer}`}*/}
          {/*  >*/}
          {/*    AI-generated content may be incorrect*/}
          {/*  </span>*/}
          {/*</Stack.Item>*/}

          {answer.citations !== undefined && answer.citations.length > 0 && (
            <Stack.Item aria-label="References" className={styles.citationHeader}>
              <Stack style={{ width: "100%" }}>
                <Stack
                  horizontal
                  horizontalAlign="start"
                  verticalAlign="center"
                  tabIndex={0}
                  onClick={handleChevronClick}
                  role="button"
                  onKeyDown={(e) =>
                    e.key === " " || e.key === "Enter"
                      ? handleChevronClick()
                      : null
                  }
                  data-testid="toggle-citations-list"
                >
                  <Text className={styles.accordionTitle}>
                    <span data-testid="no-of-references">
                      {answer.citations!.length > 1
                        ? answer.citations!.length + " references"
                        : "1 reference"}
                    </span>
                  </Text>
                  <FontIcon
                    className={styles.accordionIcon}
                    iconName={
                      chevronIsExpanded ? "ChevronDown" : "ChevronRight"
                    }
                    aria-hidden={false}
                    data-testid="chevron-icon"
                  />
                </Stack>
              </Stack>
            </Stack.Item>
          )}
        </Stack>
        {chevronIsExpanded && (
          <div
            data-testid="citations-container"
            ref={refContainer}
            style={{
              marginTop: 8,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              gap: "4px",
              maxWidth: "100%",
            }}
          >
            {answer.citations!.map((citation, idx) => {
              return (
                <span
                  data-testid="citation-block"
                  role="button"
                  onKeyDown={(e) =>
                    e.key === " " || e.key === "Enter"
                      ? onCitationClicked(citation)
                      : () => { }
                  }
                  tabIndex={0}
                  title={createCitationFilepath(citation, ++idx)}
                  key={idx}
                  onClick={() => onCitationClicked(citation)}
                  className={styles.citationContainer}
                >
                  <div className={styles.citation} key={idx}>
                    {idx}
                  </div>
                  {createCitationFilepath(citation, idx)}
                </span>
              );
            })}
          </div>
        )}

        {(timestamp || model) && (
          <div className={styles.answerTimestamp}>
            {model && <span>{model.toUpperCase()}</span>}{model && timestamp ? ' | ' : ''}{timestamp}
          </div>
        )}

        {answer.proposedAnswers && answer.proposedAnswers.length > 0 && (
          <Stack horizontal className={styles.answerFooter} verticalAlign="start">
            <Stack.Item aria-label="Suggested follow-up questions" className={styles.suggestedQuestionsHeader}>
              <Stack style={{ width: "100%" }}>
                <Stack
                  horizontal
                  horizontalAlign="start"
                  verticalAlign="center"
                  tabIndex={0}
                  onClick={() => setIsSuggestedQuestionsExpanded(!isSuggestedQuestionsExpanded)}
                  role="button"
                  onKeyDown={(e) =>
                    e.key === " " || e.key === "Enter"
                      ? setIsSuggestedQuestionsExpanded(!isSuggestedQuestionsExpanded)
                      : null
                  }
                  data-testid="toggle-suggested-questions"
                >
                  <Text className={styles.accordionTitle}>
                    <span data-testid="no-of-suggested-questions">
                      You may also want to know...
                    </span>
                  </Text>
                  <FontIcon
                    className={styles.accordionIcon}
                    iconName={
                      isSuggestedQuestionsExpanded ? "ChevronDown" : "ChevronRight"
                    }
                    aria-hidden={false}
                    data-testid="suggested-questions-chevron"
                  />
                </Stack>
              </Stack>
            </Stack.Item>
          </Stack>
        )}
        {isSuggestedQuestionsExpanded && answer.proposedAnswers && answer.proposedAnswers.length > 0 && (
          <div
            data-testid="suggested-questions-container"
            style={{
              marginTop: 8,
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: "6px",
              maxWidth: "100%",
            }}
          >
            {answer.proposedAnswers.map((question, idx) => (
              <span
                data-testid={`suggested-question-${idx}`}
                role="button"
                tabIndex={0}
                key={idx}
                onClick={() => {
                  if (suggestedQuestionSubmittedRef.current) return;
                  suggestedQuestionSubmittedRef.current = true;
                  onSuggestedQuestionClicked?.(question);
                }}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    if (suggestedQuestionSubmittedRef.current) return;
                    suggestedQuestionSubmittedRef.current = true;
                    onSuggestedQuestionClicked?.(question);
                  }
                }}
                className={styles.suggestedQuestionContainer}
              >
                <FontIcon className={styles.suggestedQuestionIcon} iconName="ChatBot" />
                {question}
              </span>
            ))}
          </div>
        )}

      </MyStackComponent>

      {isExportModalOpen && (
        <ExportModal
          isOpen={isExportModalOpen}
          onDismiss={() => setIsExportModalOpen(false)}
          conversationId={answer.conversationId}
          messageId={answer.messageId}
        />
      )}
    </>
  );
};

